import { defineComponent, ref, toRef, reactive, computed, PropType, watch, onMounted, inject, provide, ComputedRef, nextTick, defineProps, defineEmit } from 'vue';

import { FilterSelectionLocation, FilterSelectionLocationEntry } from '@Types/FilterSelections';
import { LocationGroup } from '@Components/LocationPicker/Types/Data';
import { ActiveFilter } from '@Components/SearchBar/ActiveFilter';
import appTranslations from "@Components/LocationPicker/Translations/VueTranslations";
import searchTranslations from '@Apps/Search/Translations/VueTranslations';

import { fetchLocationData } from '@Composables/LocationHelpers';
import * as searchComposables from '@Composables/SearchComposables';
import CountryComponent from './CountryComponent.vue';
import LocationEntryTSVM from '@Types/search/LocationEntryTSVM';
import LocationFilterSetting from '@Types/search/LocationFilterSetting';
import * as locationPickerComposables from './LocationPicker2023ClearLocationComposables';


export default defineComponent({

    components: { CountryComponent },

    props: {

        filterSelectionLocation: {
            type: Object as PropType<FilterSelectionLocation>,
            required: true
        },

        locationNextAsConfirm: {
            type: Boolean,
            default: false
        },

        hideConfirmButton: {
            type: Boolean,
            required: false
        },
        // selecting a location will trigger the location confirmed event.
        // will not automatically hide the confirm button.
        isAutoConfirm: {
            type: Boolean,
            required: false
        },

        // will change the styling of the action buttons to be "inline",
        // and they will not be sticky (on mobile), and use a smaller display style.
        isInlineStyled: {
            type: Boolean,
            required: false
        }
    },

    emits: ['locationConfirmed', 'onClearLocationInput', 'onClearLandingPageFilters'],

    //async 
    setup(props, context) {


        // static, cached list of countries and regions.
        const staticCountries = searchComposables.getSearchMetaDefinitions().countriesWithRegions;


        // settings specific from the setup / meta definitions.

        // landingpage currenlty not needed for display
        //const landingPageId = ref(searchComposables.getSearchMetaDefinitions().landingPageId);
        //const isLandingPage = computed(() => landingPageId.value != null && landingPageId.value != "");

       


        // in theory this is set once, on load, but we might in the future want to change this prop
        // to prevent map search to be reloaded to clear landing page data.
        // so if we 'ref' it, this is already reactive if we implement that change in the future.

        const currentLocationFilterSetting = computed(() => {

            return searchComposables.getSearchMetaDefinitions().locationFilterSetting;

        });

        // current location object with custom name and selection array
        const filterSelectionLocation = toRef(props, "filterSelectionLocation");

        // provide to children and grand children
        provide("filterSelectionLocation", filterSelectionLocation);

        const toggledCountries = ref<Set<number>>(new Set());





        const onToggleCountry = (countryId: number) => {

            if (toggledCountries.value.has(countryId)) {
                toggledCountries.value.delete(countryId);
            }
            else {
                toggledCountries.value.add(countryId);
            }

            // Force a re-render by re-assigning the ref's value:
            // delete/add do not trigger a reactivy update

            toggledCountries.value = new Set(toggledCountries.value);
        };

        const onSelectCountry = (country: LocationEntryTSVM) => {

            clearSelection();
            currentSelectedLocation.value.countryName = country.label;
            currentSelectedLocation.value.countryValueId = country.id;
            currentSelectedLocation.value.countryNoPreference = false;

            if (props.isAutoConfirm) {
                locationConfirmed();
            }

        };
        const onSelectRegion = (country: LocationEntryTSVM, region: LocationEntryTSVM) => {

            clearSelection();
            currentSelectedLocation.value.countryName = country.label;
            currentSelectedLocation.value.countryValueId = country.id;
            currentSelectedLocation.value.countryNoPreference = false;

            currentSelectedLocation.value.regionName = region.label;
            currentSelectedLocation.value.regionValueId = region.id;
            currentSelectedLocation.value.regionNoPreference = false;

            if (props.isAutoConfirm) {
                locationConfirmed();
            }
        };




        // first item in the location selection array
        const currentSelectedLocation = computed(() => {
            return filterSelectionLocation.value.selection[0];
        });

        const clearSelection = () => {
            // clear entire selection and set country selection
            filterSelectionLocation.value.selection = new Array<FilterSelectionLocationEntry>();
            filterSelectionLocation.value.selection.push(new FilterSelectionLocationEntry());

        };



        // depending on the location filter setting, we might need to adjust the visible location selection.
        // currently we only support zoom on countries, in that case we filter the countries list automatically.
        // and ideally the country would be opened..

        // Case auto => no changes
        // Case Zoom country => filter country list, allow region selection, 'alle landen' should clear all filters..



        if (currentLocationFilterSetting.value == LocationFilterSetting.ZoomCountry) {

            if (filterSelectionLocation.value.hasValue() && filterSelectionLocation.value.selection[0].countryValueId) {


                // toggle the coutry to be open on first view.
                toggledCountries.value.add(filterSelectionLocation.value.selection[0].countryValueId);

            }
        }

        const disableLocationSelections = ref(false);

        if (
            currentLocationFilterSetting.value == LocationFilterSetting.Prevent ||
            currentLocationFilterSetting.value == LocationFilterSetting.Clear) {

            // in this case we would like to hide all options, and possibly replace by custom contents.
            disableLocationSelections.value = true;
        }


        var countries = computed(() => {


            if (currentLocationFilterSetting.value == LocationFilterSetting.ZoomCountry) {

                if (filterSelectionLocation.value.hasValue() && filterSelectionLocation.value.selection[0].countryValueId) {
                    // only the selected country, e.g. from landing page,
                    // should be displayed.

                    return staticCountries.filter(c => c.id == filterSelectionLocation.value.selection[0].countryValueId);

                }
            }
            else if (disableLocationSelections.value) {

                return [];

            }


            return staticCountries;
        });


        const hideConfirmButton = computed(() => {

            if (props.hideConfirmButton) {
                return true;
            }

            // if not always hidden, we may still want to hide
            // if no selection is allowed. this is mainly to prevent
            // confusion for the user that they should 'confirm' / search
            // with the current selection.
            else return disableLocationSelections.value;

        });

        const locationConfirmed = () => {


            context.emit('locationConfirmed');
        }


        /**
        * Called when the user explicitly sets no preference for country. This button is currently labeled :"Search in all countries".
        * However this button will behave differently if a certain location filter setting is set.
        * */
        const onSelectNoPreference = () => {


            const determinedAction = locationPickerComposables.determineClearLocationFilterAction();

            if (determinedAction == locationPickerComposables.ClearLocationAction.ResetEntireFilter) {

                // this will clear the filter, and reload 
                clearLandingPageFilters();
                return;
            }


            clearSelection();
            currentSelectedLocation.value.countryNoPreference = true;

            locationConfirmed();
        }


        // should be used from a simple button to clear the current location
        const resetLocationFilter = () => {
            context.emit('onClearLocationInput');
        }


        const clearLandingPageFilters = () => {
            context.emit('onClearLandingPageFilters');
        }

        const hasMultipleLocationSelections = computed(() => {
            return filterSelectionLocation.value.selection.length > 1;
        });

        /**
         * used to determine if the 'confirm' button should be disabled or not,
         * a selection can be both 'no country preference',  and an actual selection.
         */
        const hasSelection = computed(() => {

            if (filterSelectionLocation.value.hasValue()) {


                return filterSelectionLocation.value.selection[0].hasSelection() || filterSelectionLocation.value.selection[0].countryNoPreference;

            }

            return false;

        });





        const noPreferenceButtonLabel = computed(() => {

            // the label depends on some settings, most notably:

            if (disableLocationSelections.value) {

                return searchTranslations.searchApp_Label_ClearAllFilters;
            }
            else {
                return appTranslations.locationPicker_Label_SearchInAllCountries;
            }
        });

        const currentReadOnlySelection = computed(() => {

            // get all countries
            // then add all regions or cities

            //to the country

            let selection = new Array<LocationEntryTSVM>();

            if (!filterSelectionLocation.value.hasValue()) {

                return selection;
            }

            for (let country of filterSelectionLocation.value.selection) {

                // add country (if not set?)


                var existingCountry = selection.find(s => s.id == country.countryValueId);

                //@ts-ignore for constructor params
                var countryVM = existingCountry ?? new LocationEntryTSVM();

                if (!existingCountry) {
                    selection.push(countryVM);
                    countryVM.id = country.countryValueId!;
                    countryVM.subEntries = new Array<LocationEntryTSVM>();

                }
                countryVM.label = country.countryName!;

                var sc = staticCountries.find(c => c.id == country.countryValueId);
                if (sc) {
                    countryVM.iconUrl = sc.iconUrl;
                }

                // if city is set, then add city, otherwise add region
                if (country.cityValueId) {

                  
                    //@ts-ignore for constructor params
                    let cityVM = new LocationEntryTSVM();
                    cityVM.label = country.cityName!;
                    countryVM.subEntries.push(cityVM);

                } else if (country.regionValueId) {

                 
                    //@ts-ignore for constructor params
                    let regionVM = new LocationEntryTSVM();
                    regionVM.label = country.regionName!;
                    countryVM.subEntries.push(regionVM);
                }

            }

            return selection;

        });

        return {

            currentLocationFilterSetting,
            disableLocationSelections,
            currentReadOnlySelection,

            countries,
            toggledCountries,
            onToggleCountry,
            onSelectCountry,
            onSelectRegion,

            onSelectNoPreference,
            locationConfirmed,
            hasSelection,

            hideConfirmButton,

            //locationPicker_Text_ChooseLocationYourself,
            //locationPicker_Btn_ClearAllLocations,
            //locationPicker_Btn_ChooseLocation: appTranslations.locationPicker_Btn_ChooseLocation,
            //locationPicker_Title_SelectLocations: appTranslations.locationPicker_Title_SelectLocations,
            //locationPicker_Label_ChooseCountry: appTranslations.locationPicker_Label_ChooseCountry,
            //locationPicker_Label_SearchInAllCountries: appTranslations.locationPicker_Label_SearchInAllCountries,
            //locationPicker_Label_ChooseRegion: appTranslations.locationPicker_Label_ChooseRegion,
            //locationPicker_Label_ChooseCity: appTranslations.locationPicker_Label_ChooseCity,

            //locationPickerLabelSearchByPlace,
            //locationPickerLabelSearchEverywhere,
            //locationPickerLabelSearchEverywhereLocationParent

            resxTitle: appTranslations.locationPicker_Title_Main,
            resxConfirmButton: props.locationNextAsConfirm ? appTranslations.locationPicker_Btn_ConfirmAlt : appTranslations.locationPicker_Btn_Confirm,
            noPreferenceButtonLabel,
        };

    },

});
